/**
 * MobileCameraCapture — Cámara en-app a pantalla completa para el perito.
 *
 * Sustituye al input `<input type=file capture>` nativo (que sólo deja sacar
 * UNA foto y volver). Aquí mantenemos la cámara abierta de forma continua,
 * el perito dispara todas las fotos que necesite y CADA FOTO SE SUBE AL
 * INSTANTE EN SEGUNDO PLANO (no esperamos al "Listo"). Así, si pierde
 * cobertura mientras dispara, ya hay parte subida.
 *
 * Indicadores visuales por thumbnail:
 *  - spinner ámbar mientras sube
 *  - check verde cuando la subida termina OK
 *  - aviso rojo si falla (con botón para reintentar)
 *
 * Props:
 *  - `uploadOne(blob) => Promise<string>` (REQUERIDA): callback que sube una
 *    sola foto y devuelve el `attachment_id` resuelto por el backend.
 *  - `deleteOne(attId) => Promise<void>` (opcional): si el perito borra una
 *    miniatura YA subida, se llamará para eliminarla del backend.
 *  - `onDone()`: el perito pulsa "Listo" — la cámara se cierra. Se pasa la
 *    lista de attIds (sólo los subidos OK) por si el padre quiere mostrar
 *    un toast con el total.
 *  - `onCancel()`: cerrar sin más. Los uploads ya iniciados se completarán
 *    en segundo plano (no se cancelan para no perder fotos).
 */
import { useEffect, useRef, useState } from "react";
import {
  X, Check, Camera, ArrowsClockwise, Trash, Lightning, LightningSlash,
  CircleNotch, Warning, ArrowCounterClockwise,
} from "@phosphor-icons/react";

export default function MobileCameraCapture({ uploadOne, deleteOne, onDone, onCancel }) {
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const canvasRef = useRef(null);
  const [facing, setFacing] = useState("environment");
  // Cada disparo: { ts, blob, url, status: 'uploading'|'uploaded'|'error', attId?, error? }
  const [shots, setShots] = useState([]);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [torchOn, setTorchOn] = useState(false);
  const [hasTorch, setHasTorch] = useState(false);

  // Arranque del stream cuando cambia el facingMode
  useEffect(() => {
    let cancelled = false;
    async function start() {
      stopStream();
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: {
            facingMode: { ideal: facing },
            width: { ideal: 1920 },
            height: { ideal: 1080 },
          },
          audio: false,
        });
        if (cancelled) {
          stream.getTracks().forEach((t) => t.stop());
          return;
        }
        streamRef.current = stream;
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          await videoRef.current.play().catch(() => {});
        }
        // Detectar capacidad de linterna
        const track = stream.getVideoTracks()[0];
        const caps = (track?.getCapabilities && track.getCapabilities()) || {};
        setHasTorch(!!caps.torch);
        setTorchOn(false);
      } catch (e) {
        console.error("getUserMedia error:", e);
        setError(
          e?.name === "NotAllowedError"
            ? "Permiso de cámara denegado. Actívalo en los ajustes del navegador."
            : "No se pudo acceder a la cámara del dispositivo.",
        );
      }
    }
    start();
    return () => { cancelled = true; stopStream(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [facing]);

  // Liberar URLs blob al desmontar (memoria móvil)
  useEffect(() => () => {
    shots.forEach((s) => URL.revokeObjectURL(s.url));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function stopStream() {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
  }

  const toggleTorch = async () => {
    if (!hasTorch) return;
    const track = streamRef.current?.getVideoTracks()?.[0];
    if (!track) return;
    try {
      const next = !torchOn;
      await track.applyConstraints({ advanced: [{ torch: next }] });
      setTorchOn(next);
    } catch (e) {
      console.warn("torch toggle:", e);
    }
  };

  const capture = async () => {
    if (busy) return;
    const video = videoRef.current;
    if (!video || video.readyState < 2) return;
    setBusy(true);
    try {
      const w = video.videoWidth;
      const h = video.videoHeight;
      const canvas = canvasRef.current || document.createElement("canvas");
      canvas.width = w;
      canvas.height = h;
      const ctx = canvas.getContext("2d");
      ctx.drawImage(video, 0, 0, w, h);
      const blob = await new Promise((resolve) => canvas.toBlob(resolve, "image/jpeg", 0.85));
      if (!blob) return;
      const url = URL.createObjectURL(blob);
      const ts = Date.now() + Math.random();   // garantizamos unicidad si dispara muy rápido
      setShots((prev) => [...prev, { ts, blob, url, status: "uploading" }]);
      try { navigator.vibrate?.(30); } catch {}
      // Subida en background: no bloqueamos siguientes capturas
      uploadShot(ts, blob);
    } finally {
      setBusy(false);
    }
  };

  // Lanza una subida individual; actualiza el estado del shot según el resultado.
  const uploadShot = async (ts, blob) => {
    if (!uploadOne) return;
    try {
      const attId = await uploadOne(blob);
      setShots((prev) => prev.map((s) => s.ts === ts
        ? { ...s, status: "uploaded", attId, error: null } : s));
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || "Error";
      setShots((prev) => prev.map((s) => s.ts === ts
        ? { ...s, status: "error", error: String(msg).slice(0, 60) } : s));
    }
  };

  const retryUpload = (ts) => {
    const shot = shots.find((s) => s.ts === ts);
    if (!shot) return;
    setShots((prev) => prev.map((s) => s.ts === ts
      ? { ...s, status: "uploading", error: null } : s));
    uploadShot(ts, shot.blob);
  };

  const removeShot = async (ts) => {
    const shot = shots.find((s) => s.ts === ts);
    if (!shot) return;
    // Si ya está subida al backend, también la borramos allí
    if (shot.status === "uploaded" && shot.attId && deleteOne) {
      try { await deleteOne(shot.attId); } catch {}
    }
    setShots((prev) => {
      const found = prev.find((s) => s.ts === ts);
      if (found) URL.revokeObjectURL(found.url);
      return prev.filter((s) => s.ts !== ts);
    });
  };

  // Esperar a que todas las subidas en curso terminen (o fallen) antes de cerrar
  const waitPendingUploads = async () => {
    const start = Date.now();
    while (Date.now() - start < 15_000) {
      const stillUploading = shots.some((s) => s.status === "uploading");
      if (!stillUploading) return;
      await new Promise((r) => setTimeout(r, 250));
    }
  };

  const done = async () => {
    // Pequeña espera para no cerrar mientras quede una subida casi terminada,
    // pero capamos por seguridad para no bloquear al perito.
    await waitPendingUploads();
    stopStream();
    const uploaded = shots.filter((s) => s.status === "uploaded");
    onDone?.(uploaded.map((s) => s.attId));
  };

  const cancel = () => {
    stopStream();
    // No revocamos URLs ni cancelamos subidas: si ya subieron, mejor mantenerlas.
    onCancel?.();
  };

  if (error) {
    return (
      <div className="fixed inset-0 z-50 bg-black text-white flex flex-col items-center justify-center p-6 text-center"
           data-testid="mobile-camera-error">
        <Camera className="w-12 h-12 mb-3 text-rose-400" weight="duotone" />
        <div className="font-semibold mb-1">No se pudo abrir la cámara</div>
        <div className="text-[12px] text-zinc-400 mb-4">{error}</div>
        <button onClick={cancel}
                className="bg-white text-foreground text-[13px] font-semibold px-5 py-2.5 rounded-md">
          Cerrar
        </button>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 bg-black flex flex-col"
         data-testid="mobile-camera-capture">
      {/* Top bar */}
      <div className="absolute top-0 inset-x-0 z-10 flex items-center justify-between p-3 bg-gradient-to-b from-black/70 to-transparent text-white">
        <button onClick={cancel}
                data-testid="mobile-camera-cancel"
                className="p-2 rounded-full bg-black/40 backdrop-blur">
          <X className="w-5 h-5" weight="bold" />
        </button>
        <div className="text-[12px] font-semibold bg-black/40 px-3 py-1 rounded-full backdrop-blur"
             data-testid="mobile-camera-counter">
          {shots.length} foto{shots.length === 1 ? "" : "s"}
        </div>
        <div className="flex gap-2">
          {hasTorch && (
            <button onClick={toggleTorch}
                    data-testid="mobile-camera-torch"
                    className={`p-2 rounded-full backdrop-blur ${torchOn ? "bg-amber-400 text-black" : "bg-black/40 text-white"}`}>
              {torchOn ? <Lightning className="w-5 h-5" weight="fill" /> : <LightningSlash className="w-5 h-5" weight="bold" />}
            </button>
          )}
          <button onClick={() => setFacing((f) => f === "environment" ? "user" : "environment")}
                  data-testid="mobile-camera-flip"
                  className="p-2 rounded-full bg-black/40 backdrop-blur text-white">
            <ArrowsClockwise className="w-5 h-5" weight="bold" />
          </button>
        </div>
      </div>

      {/* Live preview */}
      <video
        ref={videoRef}
        playsInline
        muted
        autoPlay
        className="flex-1 w-full h-full object-cover bg-black"
        data-testid="mobile-camera-video"
      />
      <canvas ref={canvasRef} className="hidden" />

      {/* Bottom: thumbnail strip + shutter + done */}
      <div className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-black to-black/70 pt-3 pb-5 px-3">
        {shots.length > 0 && (
          <div className="flex gap-2 overflow-x-auto pb-3 -mx-1 px-1 snap-x"
               data-testid="mobile-camera-strip">
            {shots.map((s) => (
              <ShotThumb key={s.ts} shot={s}
                          onRemove={() => removeShot(s.ts)}
                          onRetry={() => retryUpload(s.ts)} />
            ))}
          </div>
        )}
        <div className="flex items-center justify-center gap-6">
          <div className="w-12" />  {/* spacer simétrico */}
          {/* Botón disparador */}
          <button
            onClick={capture}
            disabled={busy}
            data-testid="mobile-camera-shutter"
            className={`w-20 h-20 rounded-full border-4 border-white flex items-center justify-center transition-transform ${busy ? "scale-90 opacity-70" : "active:scale-95"}`}
          >
            <span className="w-16 h-16 rounded-full bg-white" />
          </button>
          {shots.length > 0 ? (
            <DoneButton
              shots={shots}
              onClick={done}
            />
          ) : (
            <div className="w-14" /> /* spacer */
          )}
        </div>
      </div>
    </div>
  );
}

function DoneButton({ shots, onClick }) {
  const total = shots.length;
  const uploaded = shots.filter((s) => s.status === "uploaded").length;
  const uploading = shots.filter((s) => s.status === "uploading").length;
  const failed = shots.filter((s) => s.status === "error").length;
  const allOk = total > 0 && uploaded === total;
  return (
    <button onClick={onClick}
            data-testid="mobile-camera-done"
            className={`min-w-14 px-3 h-14 rounded-full flex flex-col items-center justify-center shadow-lg active:scale-95 text-white ${
              allOk ? "bg-emerald-500" :
              failed > 0 ? "bg-amber-600" : "bg-emerald-500/80"
            }`}>
      <Check className="w-5 h-5" weight="bold" />
      <span className="text-[9px] font-bold mt-0.5">
        {allOk ? "LISTO" : `${uploaded}/${total}`}
      </span>
    </button>
  );
}

function ShotThumb({ shot, onRemove, onRetry }) {
  return (
    <div className="relative flex-shrink-0 w-16 h-16 rounded-md overflow-hidden border-2 border-white/40 snap-start"
         data-testid={`mobile-camera-thumb-${shot.ts}`}>
      <img src={shot.url} alt="" className="w-full h-full object-cover" />
      {/* Overlay con estado de subida */}
      {shot.status === "uploading" && (
        <div className="absolute inset-0 bg-black/40 flex items-center justify-center"
             data-testid={`mobile-camera-thumb-uploading-${shot.ts}`}>
          <CircleNotch className="w-5 h-5 text-amber-300 animate-spin" weight="bold" />
        </div>
      )}
      {shot.status === "uploaded" && (
        <div className="absolute bottom-0 right-0 w-5 h-5 bg-emerald-500 rounded-tl-md flex items-center justify-center"
             data-testid={`mobile-camera-thumb-uploaded-${shot.ts}`}>
          <Check className="w-3 h-3 text-white" weight="bold" />
        </div>
      )}
      {shot.status === "error" && (
        <button onClick={onRetry}
                title={shot.error || "Reintentar"}
                data-testid={`mobile-camera-thumb-retry-${shot.ts}`}
                className="absolute inset-0 bg-rose-700/70 flex items-center justify-center text-white">
          <ArrowCounterClockwise className="w-5 h-5" weight="bold" />
        </button>
      )}
      {shot.status === "error" && (
        <div className="absolute top-0 left-0 right-0 bg-rose-700 text-white text-[8px] font-bold text-center px-0.5 leading-none py-0.5">
          <Warning className="w-2.5 h-2.5 inline mr-0.5" weight="bold" />
        </div>
      )}
      <button onClick={onRemove}
              data-testid={`mobile-camera-remove-${shot.ts}`}
              className="absolute -top-1 -right-1 w-5 h-5 bg-rose-600 text-white rounded-full inline-flex items-center justify-center shadow-md">
        <Trash className="w-3 h-3" weight="bold" />
      </button>
    </div>
  );
}
